import { type Tag, type TagCategory, TagCategoryToDisplayName } from "@albertine/types";
import { tagDisplayName } from "albertine-shared-web";
import { useEffect, useState } from "react";
import {
    type CategoriesWithUncategorized,
    OfferingCategoryUncategorizedKey,
} from "../../common/types/OfferingCategory";
import { Flex } from "../Common/Flex";
import SelectTags from "./SelectTags";
import "./SelectTagsWithCategory.css";
import { getCategorySpecificTags } from "../../common/utils/tags.util";
import Label from "../Common/Label";
import Paragraph from "../Common/Paragraph";
import { TagToJSXElement } from "./TagsWithCategories";

type Props = {
    offeringCategory: CategoriesWithUncategorized | undefined;
    values: string[] | Tag[];
    onChange: (newValues: Tag[]) => void;
};

function SelectTagsWithCategory(props: Props) {
    const { offeringCategory, values, onChange } = props;
    const [state, setState] = useState<Tag[]>((values as Tag[]) || []);

    useEffect(() => {
        onChange(state);
    }, [state]);

    const categorySpecificTags = getCategorySpecificTags(offeringCategory);

    if (!offeringCategory || offeringCategory === OfferingCategoryUncategorizedKey) {
        return (
            <Paragraph.Medium>
                Choose a category to see the recommended tags.
            </Paragraph.Medium>
        );
    }

    if (Object.entries(categorySpecificTags).length === 0) {
        return (
            <Paragraph.Medium.N50>
                No tags available for this category.
            </Paragraph.Medium.N50>
        );
    }

    return (
        <Flex.Column gap="large">
            {Object.entries(categorySpecificTags).map((_tags) => {
                const tagCategory = _tags[0] as TagCategory;
                const categoryTags = _tags[1];
                const tagCategoryName = TagCategoryToDisplayName[tagCategory];
                const tagIds = Object.values(categoryTags);
                const tagsForSelection = tagIds.map((tag) => ({
                    id: tag,
                    value: tagDisplayName(tag, "en"),
                    displayValue: TagToJSXElement[tag],
                    selected: state.includes(tag) || false,
                }));

                return (
                    <Flex.Row key={tagCategory} alignItems="start">
                        <div className="select-tags__category">
                            <Label.Small.SemiBold>
                                {tagCategoryName}
                            </Label.Small.SemiBold>
                        </div>
                        <div>
                            <SelectTags<Tag>
                                values={tagsForSelection}
                                onChange={(newValues) => {
                                    const selected = newValues
                                        .filter((it) => it.selected)
                                        .map((it) => it.id);
                                    setState((prevState) => [
                                        ...prevState.filter(
                                            (it) => !tagIds.includes(it),
                                        ),
                                        ...selected,
                                    ]);
                                }}
                                selectMultiple
                            />
                        </div>
                    </Flex.Row>
                );
            })}
        </Flex.Column>
    );
}

export default SelectTagsWithCategory;
